import { computed, ref, watch, type Ref } from 'vue';
import { useCustomToast, useInfiniteScrollObserver } from '@/shared/lib';

export const INFINITE_SCROLL_PAGE_SIZE = 30;

export interface PaginatedListMetadata {
  page: number;
  total: number;
  totalPages: number;
}

export interface UsePaginatedListFetchOptions<TItem, TMetadata extends PaginatedListMetadata> {
  page: Ref<number>;
  isInfiniteScrollMode: Ref<boolean>;
  errorMessage: string;
  requestPage: (targetPage: number) => Promise<{ data: TItem[]; metadata: TMetadata }>;
}

export function usePaginatedListFetch<TItem, TMetadata extends PaginatedListMetadata>(
  options: UsePaginatedListFetchOptions<TItem, TMetadata>
) {
  const { page, isInfiniteScrollMode, errorMessage, requestPage } = options;
  const { showErrorToast } = useCustomToast();

  const items = ref([]) as Ref<TItem[]>;
  const metadata = ref(null) as Ref<TMetadata | null>;
  const loading = ref(false);
  const loadingMore = ref(false);
  const hasMoreItems = ref(false);
  const error = ref<string | null>(null);
  const loadedPage = ref(1);
  let currentQueryId = 0;

  const isInitialLoading = computed(() => loading.value && metadata.value === null);

  async function fetch() {
    const queryId = ++currentQueryId;
    loading.value = true;
    error.value = null;

    try {
      const result = await requestPage(page.value);
      if (queryId !== currentQueryId) return;
      items.value = result.data;
      metadata.value = result.metadata;
      loadedPage.value = page.value;
      hasMoreItems.value =
        isInfiniteScrollMode.value && result.metadata.page < result.metadata.totalPages;
    } catch {
      if (queryId !== currentQueryId) return;
      items.value = [];
      metadata.value = null;
      hasMoreItems.value = false;
      error.value = errorMessage;
    } finally {
      if (queryId === currentQueryId) {
        loading.value = false;
      }
    }
  }

  async function loadMore() {
    if (!isInfiniteScrollMode.value || loadingMore.value || !hasMoreItems.value) return;

    const queryId = ++currentQueryId;
    const nextPage = loadedPage.value + 1;
    loadingMore.value = true;

    try {
      const result = await requestPage(nextPage);
      if (queryId !== currentQueryId) return;
      items.value = [...items.value, ...result.data];
      metadata.value = result.metadata;
      loadedPage.value = nextPage;
      hasMoreItems.value = nextPage < result.metadata.totalPages;
    } catch {
      if (queryId !== currentQueryId) return;
      hasMoreItems.value = false;
      showErrorToast({ title: errorMessage, type: 'error' });
    } finally {
      loadingMore.value = false;
    }
  }

  useInfiniteScrollObserver({
    enabled: isInfiniteScrollMode,
    loadingMore,
    hasMore: hasMoreItems,
    onLoadMore: loadMore,
  });

  watch(metadata, (value) => {
    if (!value || isInfiniteScrollMode.value) return;
    if (value.total > 0 && value.page > value.totalPages) {
      page.value = value.totalPages;
    }
  });

  return {
    items,
    metadata,
    loading,
    loadingMore,
    hasMoreItems,
    error,
    isInitialLoading,
    loadedPage,
    fetch,
    loadMore,
  };
}
